import React from 'react';
import clsx from 'clsx';
import { formatCOP } from '../../utils/currency';

interface OrderPaymentStepProps {
  paymentMethod: string;
  setPaymentMethod: (method: string) => void;
  discount: number;
  setDiscount: (discount: number) => void;
  deliveryCost: number;
  setDeliveryCost: (cost: number) => void;
  notes: string;
  setNotes: (notes: string) => void;
  subtotal: number;
  applyQuickDiscount: (percentage: number) => void;
  onBack: () => void;
  onSubmit: () => void;
  isLoading: boolean;
}

export const OrderPaymentStep: React.FC<OrderPaymentStepProps> = ({
  paymentMethod,
  setPaymentMethod,
  discount,
  setDiscount,
  deliveryCost,
  setDeliveryCost,
  notes,
  setNotes,
  subtotal,
  applyQuickDiscount,
  onBack,
  onSubmit,
  isLoading
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="bg-white border-b border-zinc-200 px-3 py-2">
        <h2 className="text-sm font-bold text-gray-900">Confirmar Venta</h2>
      </div>

      <div className="p-3 space-y-2.5">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Método de Pago
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2" role="radiogroup" aria-label="Método de pago">
            {[
              { id: 'efectivo', icon: '', label: 'Efectivo' },
              { id: 'nequi', icon: '', label: 'Nequi' },
              { id: 'daviplata', icon: '', label: 'Daviplata' },
              { id: 'pse', icon: '', label: 'PSE' },
              { id: 'transferencia', icon: '', label: 'Transferencia' },
              { id: 'tarjeta', icon: '', label: 'Tarjeta' }
            ].map(method => (
              <button
                key={method.id}
                type="button"
                onClick={() => setPaymentMethod(method.id)}
                role="radio"
                aria-checked={paymentMethod === method.id}
                className={clsx(
                  "relative py-2 px-3 rounded-lg border font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2",
                  paymentMethod === method.id
                    ? "border-zinc-900 bg-zinc-900 text-white shadow-md"
                    : "border-gray-200 hover:border-zinc-400 bg-white text-gray-700 hover:shadow-sm"
                )}
              >
                <div className="text-xs">{method.label}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            🎁 Descuento
          </label>
          <div className="flex gap-2 mb-2">
            {[5, 10, 15, 20].map(perc => (
              <button
                key={perc}
                onClick={() => applyQuickDiscount(perc)}
                className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-all font-medium text-sm"
              >
                {perc}%
              </button>
            ))}
            <button
              onClick={() => setDiscount(0)}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-medium text-sm"
            >
              Sin descuento
            </button>
          </div>
          <input
            type="number"
            value={discount}
            onChange={(e) => setDiscount(Math.max(0, Number(e.target.value)))}
            className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
            placeholder="Ingresa monto manual"
            min="0"
          />
          {discount > 0 && (
            <p className="text-xs text-zinc-600 mt-1.5">
              Descuento aplicado: {formatCOP(discount)} ({((discount/subtotal)*100).toFixed(1)}%)
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            🚚 Costo de Envío
          </label>
          <input
            type="number"
            value={deliveryCost}
            onChange={(e) => setDeliveryCost(Math.max(0, Number(e.target.value)))}
            className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all"
            placeholder="0 = Recoge en tienda"
            min="0"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            📝 Notas (opcional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all resize-none"
            placeholder="Agrega notas sobre esta venta..."
          />
        </div>

        <div className="flex gap-3 pt-2">
          <button
            onClick={onBack}
            className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100 hover:border-gray-400 transition-all flex items-center justify-center gap-2 text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
            </svg>
            <span>Atrás</span>
          </button>
          <button
            onClick={onSubmit}
            disabled={isLoading}
            className="flex-1 py-2 bg-zinc-900 hover:bg-zinc-800 text-white shadow-sm transition-all rounded-lg font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-6 w-6 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Procesando...</span>
              </>
            ) : (
              <>
                <span>Confirmar Venta</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
